import {
  Col,
  Flex,
  Form,
  Input,
  Paragraph,
  Row,
  Switch,
  Textarea,
  type FormInstance,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { getInputValue } from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  createProductCategoryKeyRules,
  generateProductCategoryKeyFromName,
} from "./productCategoryKeyValidation";

export interface ProductCategoryAddEditFormValues {
  name: string;
  categoryKey: string;
  description: string;
  searchVectorKeywords: string;
  isActive: boolean;
}

interface MasterDataProductCategoryAddEditFormProps {
  form: FormInstance<ProductCategoryAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: ProductCategoryAddEditFormValues;
  onSubmit: (values: ProductCategoryAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const MasterDataProductCategoryAddEditForm = ({
  form,
  mode,
  initialValues,
  onSubmit,
  onFormValidityChange,
}: MasterDataProductCategoryAddEditFormProps) => {
  const isKeyEditedManuallyRef = useRef(false);
  const categoryKeyDisabled = useMemo(() => mode === "edit", [mode]);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const name = values.name?.trim() ?? "";
    const categoryKey = values.categoryKey?.trim() ?? "";
    const description = values.description?.trim() ?? "";
    const searchVectorKeywords = values.searchVectorKeywords?.trim() ?? "";

    const hasRequiredValues =
      name !== "" && (mode === "edit" || categoryKey !== "");

    const isDirty =
      mode === "create"
        ? true
        : name !== (initialValues.name?.trim() ?? "") ||
          description !== (initialValues.description?.trim() ?? "") ||
          searchVectorKeywords !==
            (initialValues.searchVectorKeywords?.trim() ?? "") ||
          Boolean(values.isActive) !== Boolean(initialValues.isActive);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    isKeyEditedManuallyRef.current = mode === "edit";
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, mode, updateFormValidity]);

  const nameRules = useMemo(
    () => [
      { required: true, message: "Category name is required" },
      {
        max: 255,
        message: "Category name must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const categoryKeyRules = useMemo(() => createProductCategoryKeyRules(), []);

  const descriptionRules = useMemo(
    () => [
      {
        max: 500,
        message: "Description must be less than or equal to 500 characters.",
      },
    ],
    [],
  );

  const searchVectorKeywordsRules = useMemo(
    () => [
      {
        max: 255,
        message:
          "Search vector keywords must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const syncCategoryKeyFromName = (valueOrEvent: unknown) => {
    if (categoryKeyDisabled || isKeyEditedManuallyRef.current) return;
    form.setFieldsValue({
      categoryKey: generateProductCategoryKeyFromName(
        getInputValue(valueOrEvent),
      ),
    });
    form.validateFields(["categoryKey"]).catch(() => {});
  };

  const handleCategoryNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      name: getInputValue(valueOrEvent),
    });
    syncCategoryKeyFromName(valueOrEvent);
    form.validateFields(["name"]).catch(() => {});
    updateFormValidity();
  };

  const handleCategoryKeyChange = () => {
    if (categoryKeyDisabled) return;
    isKeyEditedManuallyRef.current = true;
    form.validateFields(["categoryKey"]).catch(() => {});
    updateFormValidity();
  };

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-product-category-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="name"
              label="Category Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={nameRules}
            >
              <Input
                placeholder="Category name"
                onChange={handleCategoryNameChange}
                onInput={handleCategoryNameChange}
                onKeyUp={handleCategoryNameChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="categoryKey"
              label="Category Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={categoryKeyDisabled ? undefined : categoryKeyRules}
              helpText={
                categoryKeyDisabled
                  ? "Once category key is created, it cannot be changed."
                  : undefined
              }
            >
              <Input
                placeholder="CATEGORY-KEY"
                isDisabled={categoryKeyDisabled}
                onChange={handleCategoryKeyChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              name="description"
              label="Description"
              validateTrigger={["onChange", "onBlur"]}
              rules={descriptionRules}
            >
              <Textarea placeholder="Enter Description" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name="searchVectorKeywords"
              label="Search Vector Keywords"
              validateTrigger={["onChange", "onBlur"]}
              rules={searchVectorKeywordsRules}
            >
              <Textarea
                placeholder="Enter Keywords"
                maxLength={255}
                minRows={3}
              />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Flex
              align="start"
              gap={1}
              className="status-switch"
              direction="column"
            >
              <Paragraph className="m-0 custom-label">Status</Paragraph>
              <Form.Item name="isActive" valuePropName="isSelected" noStyle>
                <Switch
                  className="p-0 mt-1"
                  size="small"
                  aria-label="Category active status"
                />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
